export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validação de senha no frontend espelhando as regras do backend
 * (backend/src/utils/passwordValidation.ts).
 */
export const validatePasswordFrontend = (password: string): PasswordValidationResult => {
  const errors: string[] = [];

  if (!password || typeof password !== 'string') {
    return {
      isValid: false,
      errors: ['Senha é obrigatória'],
    };
  }

  const trimmedPassword = password.trim();

  if (trimmedPassword.length < 12) {
    errors.push('Senha deve ter no mínimo 12 caracteres');
  }

  if (trimmedPassword.length > 128) {
    errors.push('Senha deve ter no máximo 128 caracteres');
  }

  if (password.includes(' ')) {
    errors.push('Senha não pode conter espaços');
  }

  if (!/[A-Z]/.test(trimmedPassword)) {
    errors.push('Senha deve conter pelo menos uma letra maiúscula');
  }

  if (!/[a-z]/.test(trimmedPassword)) {
    errors.push('Senha deve conter pelo menos uma letra minúscula');
  }

  if (!/\d/.test(trimmedPassword)) {
    errors.push('Senha deve conter pelo menos um número');
  }

  if (!/[@$!%*?&]/.test(trimmedPassword)) {
    errors.push('Senha deve conter pelo menos um caractere especial (@$!%*?&)');
  }

  if (/(.)\1{4,}/.test(trimmedPassword)) {
    errors.push('Senha não pode conter mais de 4 caracteres repetidos consecutivamente');
  }

  // Regras adicionais para ficar alinhado 100% com o backend:
  // - bloquear senhas muito comuns
  // - bloquear padrões simples (sequências numéricas, alfabéticas, teclado)
  const COMMON_PASSWORDS = new Set([
    'password', '123456', '123456789', '12345678', '12345', '1234567',
    'password123', 'admin123', 'senha123', '1234567890', 'qwerty',
    'abc123', '111111', '123123', 'admin', 'letmein', 'welcome',
    'monkey', 'dragon', 'master', 'sunshine', 'football', 'iloveyou',
    'princess', 'welcome123', 'admin@123', '123456@', 'password@123',
  ]);

  const lowerPassword = trimmedPassword.toLowerCase();
  if (COMMON_PASSWORDS.has(lowerPassword)) {
    errors.push('Senha muito comum. Escolha uma senha mais segura');
  }

  const simplePatterns = [
    /(012|123|234|345|456|567|678|789|890)/,
    /(abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/i,
    /(qwerty|asdfgh|zxcvbn)/i,
  ];

  if (simplePatterns.some((pattern) => pattern.test(trimmedPassword))) {
    errors.push('Senha contém padrões muito simples. Escolha uma senha mais segura');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Alfabeto sem caracteres visualmente ambíguos (I/l/1, O/0) — a senha é só sugerida, o
// admin/usuário costuma copiar de olho ou digitar em outro dispositivo (ex.: informar por
// telefone), então evitar essa confusão vale mais do que ganhar entropia com eles.
const GENERATOR_CHARSET = {
  upper: 'ABCDEFGHJKLMNPQRSTUVWXYZ',
  lower: 'abcdefghijkmnpqrstuvwxyz',
  digits: '23456789',
  special: '@$!%*?&', // exatamente o conjunto aceito por validatePasswordFrontend, nada a mais
};

const randomIndex = (max: number): number => {
  // crypto.getRandomValues é mais forte que Math.random, mas nem todo ambiente de teste expõe
  // `crypto` global — cai pra Math.random nesse caso (a senha ainda passa pela validação real
  // logo abaixo, então a única coisa em jogo aqui é a qualidade da aleatoriedade, não a política).
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const array = new Uint32Array(1);
    crypto.getRandomValues(array);
    return array[0]! % max;
  }
  return Math.floor(Math.random() * max);
};

const randomChar = (charset: string): string => charset[randomIndex(charset.length)]!;

/**
 * Gera uma senha aleatória que já atende `validatePasswordFrontend` (mesmas regras do backend,
 * ver backend/src/utils/passwordValidation.ts). Em vez de reimplementar cada regra aqui (mínimo de
 * caracteres, sem sequência simples, sem senha comum etc.), gera candidatas e reusa a validação
 * real pra conferir — evita as duas checagens divergirem com o tempo.
 */
export const generateStrongPassword = (length = 16): string => {
  const { upper, lower, digits, special } = GENERATOR_CHARSET;
  const all = upper + lower + digits + special;

  for (let attempt = 0; attempt < 25; attempt++) {
    // Garante pelo menos 1 de cada categoria exigida, preenche o resto e embaralha.
    const chars = [
      randomChar(upper),
      randomChar(lower),
      randomChar(digits),
      randomChar(special),
      ...Array.from({ length: Math.max(length - 4, 0) }, () => randomChar(all)),
    ];
    for (let i = chars.length - 1; i > 0; i--) {
      const j = randomIndex(i + 1);
      [chars[i], chars[j]] = [chars[j]!, chars[i]!];
    }
    const candidate = chars.join('');
    if (validatePasswordFrontend(candidate).isValid) {
      return candidate;
    }
  }

  // Praticamente inatingível com o charset acima (nenhuma sequência de 3+ chars consecutivos no
  // teclado embaralhado é frequente o bastante pra falhar 25 tentativas seguidas), mas nunca
  // devolver uma senha que não passou pela validação real.
  throw new Error('Não foi possível gerar uma senha válida. Tente novamente.');
};


